import {ChangeDetectorRef, Component, HostListener, OnInit} from '@angular/core';
import {AssetRepresentation} from '../../../model/asset-representation';
import {Tenant} from '../../../model/tenant';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthGuard} from '../../../common/auth-guard.service';
import {AssetRepresentationsService} from '../../../services/asset-representations.service';
import {TenantsService} from '../../../services/tenants.service';
import {noSpecialCharacters} from '../../../shared/custom-validators';
import {AssetRepresentationPersistRequest} from '../../../model/requests/asset-representation-persist-request';
import {CommonMessagesService} from '../../../services/common-messages.service';
import {MapIconsService} from '../../../services/map-icons.service';
import {LoggedUser} from '../../../common/logged-user.service';
import {ColorReplaceService} from '../../../services/color-replace.service';

@Component({
  selector: 'app-asset-representations-form',
  templateUrl: './asset-representations-form.component.html',
  styleUrls: ['./asset-representations-form.component.scss']
})
export class AssetRepresentationsFormComponent implements OnInit {

  public representation: AssetRepresentation;

  public tenants: Tenant[];

  public representationForm: FormGroup;
  public persistingChanges: any;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: { tenantUuid: string, oldName: string };

  public targetIconColor: undefined | string;

  public imageStatusSources: Map<string, { color: string, img: string }>;
  public imageStatusTypes: string[];
  public loadingIcons: boolean = false;

  public iconHeight: number;
  public iconWidth: number;

  icon: any;

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private authGuard: AuthGuard,
              private assetRepresentationsService: AssetRepresentationsService,
              private tenantsService: TenantsService,
              private commonMessagesService: CommonMessagesService,
              public mapIconService: MapIconsService,
              private loggedUser: LoggedUser,
              private readonly colorReplaceService: ColorReplaceService,
              private readonly changeDetectorRef: ChangeDetectorRef
  ) {

    this.imageStatusSources = new Map();
    this.targetIconColor = this.mapIconService.getDefaultIconMainColor();
    this.imageStatusTypes = this.assetRepresentationsService.getImageStatusTypes();

    this.loggedUser.userPreferences.subscribe((userData) => {
      this.assetRepresentationsService.buildAssetRepresentationPreferences(userData.preferences);
      this.loadDefaultImage();
    });
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[0].path === 'new') {
        this.mode = 'create';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadCreationForm();
        });
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[0].path, urlSegment[1].path);
      }
    });
  }

  public loadDefaultImage() {
    this.loadCustomIcons('data:image/png;base64,' + this.mapIconService.getDefaultIcon(), this.mapIconService.getDefaultIconMainColor());
  }

  public loadUploadImage() {
    this.loadCustomIcons(this.icon, this.targetIconColor);
  }

  private async loadCustomIcons(iconToLoad: string, iconTargetColor: string) {
    const defaultIcon = iconToLoad;
    const promise = [];
    let result: string[];

    if (iconTargetColor === null) {
      for (const item of this.imageStatusTypes) {
        this.imageStatusSources.set(item, {
          color: this.assetRepresentationsService.getColorFromLoadedPreferences(item),
          img: iconTargetColor
        });
      }
    } else {
      for (const item of this.imageStatusTypes) {
        const image = this.colorReplaceService.changeColorImg(defaultIcon, iconTargetColor, this.assetRepresentationsService.getColorFromLoadedPreferences(item));
        promise.push(image);
      }

      result = await Promise.all(promise);

      for (let i = 0; i < result.length; i++) {
        const item = this.imageStatusTypes[i];
        this.imageStatusSources.set(item, {
          color: this.assetRepresentationsService.getColorFromLoadedPreferences(item),
          img: result[i]
        });
      }
    }

  }

  loadCreationForm(): void {
    this.representation = new AssetRepresentation();
    if (!this.authGuard.superAdminRequired()) {
      this.representation.tenant = this.tenants[0];
    }
    this.createForm(this.representation);
  }

  loadEditionForm(tenantUuid: string, name: string): void {
    this.loadingIcons = true;
    this.tenantsService.getTenantAssetRepresentation(tenantUuid, name).toPromise().then(response => {
      this.representation = response.assetRepresentation;
      this.tenants = [this.representation.tenant];
      this.editInfo = {tenantUuid: this.representation.tenant.uuid, oldName: this.representation.name};
      this.targetIconColor = this.representation.targetColor;
      if (this.representation.icon) {
        this.icon = 'data:image/png;base64,' + this.representation.icon;
        this.loadCustomIcons(this.icon, this.targetIconColor).finally(() => {
          this.loadingIcons = false;
        });
      } else {
        this.loadingIcons = false;
      }


      this.createForm(this.representation);
    });
  }

  private createForm(representation: AssetRepresentation): void {
    this.representationForm = this.formBuilder.group({
      name: [representation.name, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      description: [representation.description, [Validators.maxLength(255)]],
      tenant: [representation.tenant, [Validators.required]]
    });
    this.representationForm.markAsPending();

    if (!this.authGuard.superAdminRequired()) {
      this.representationForm.get('tenant').disable();
    }

    if (this.mode === 'edit') {
      this.representationForm.get('tenant').disable();
    }
  }

  onUploadIcon(input: HTMLInputElement): void {
    const file: File = input.files[0];
    const reader: FileReader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => {
      const img = new Image(150);
      img.onload = () => {
        this.iconWidth = img.width;
        this.iconHeight = img.height;
        this.targetIconColor = this.icon ? this.targetIconColor : '#FFFFFF';
        this.icon = reader.result;
        this.loadingIcons = true;
        this.changeDetectorRef.detectChanges();
        this.loadCustomIcons(this.icon, this.targetIconColor).finally(() => this.loadingIcons = false);
      };
      img.src = URL.createObjectURL(file);
    };
  }

  persistRepresentationData(): void {
    if (!this.representationForm.valid) {
      return;
    }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise: Promise<AssetRepresentationPersistRequest>;
    if (this.mode === 'create') {
      promise = this.assetRepresentationsService.createAssetRepresentation(persistData).toPromise();
    } else {
      promise = this.assetRepresentationsService.editAssetRepresentation(persistData).toPromise();
    }

    promise.then(() => {
      this.formSuccess = true;
      this.router.navigate([`/system/asset-representations`]);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): AssetRepresentationPersistRequest {
    const formValue = this.representationForm.getRawValue();
    return {
      name: formValue.name,
      description: formValue.description,
      tenantUuid: this.mode === 'edit' ? this.editInfo.tenantUuid : formValue.tenant.uuid,
      icon: this.icon ? (this.icon as string).split(',')[1] : null,
      oldName: this.mode === 'edit' ? this.editInfo.oldName : null,
      targetIconColor: this.icon ? this.targetIconColor : null,
    };
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.representationForm.untouched;
  }

}
